// ============================================================
// Ebek Sales CRM — My Profile
// • Profile photo upload
// • Personal details (phone + WhatsApp number)
// • Personal SMTP email setup (so CRM emails come from YOU)
// ============================================================
import React, { useEffect, useRef, useState } from "react";
import API from "../../utils/api";

const BG   = "linear-gradient(135deg,#1e1b4b 0%,#312e81 50%,#1e1b4b 100%)";
const CARD = {
  background:"rgba(255,255,255,0.07)", borderRadius:16,
  border:"1px solid rgba(255,255,255,0.12)", padding:"20px 18px", marginBottom:18,
};

const PROVIDERS = [
  { label:"Yahoo Bizmail", host:"smtp.bizmail.yahoo.com", port:465,
    hint:"For @ebekindia.com: open Yahoo Account Security → External connections → Create app password → Other → name it Ebek CRM." },
  { label:"Zoho Mail", host:"smtppro.zoho.com", port:465,
    hint:"For @ebekmed.com: sign in at accounts.zoho.com → Security → Application Specific Passwords → Generate New Password → name it Ebek CRM." },
  { label:"Gmail",   host:"smtp.gmail.com",            port:465,
    hint:"Go to Google Account → Security → 2-Step Verification → App passwords → select 'Mail' + 'Other device'." },
  { label:"Outlook", host:"smtp-mail.outlook.com",     port:587,
    hint:"Go to Microsoft Account → Security → Advanced security options → App passwords." },
  { label:"Other",   host:"",                          port:465,
    hint:"Only company-approved Yahoo or Zoho SMTP servers can be saved." },
];

const BASE_URL = process.env.REACT_APP_API_URL?.replace("/api","") || "http://localhost:4001";

export default function Profile({ onBack }) {
  const fileRef = useRef(null);

  // Profile state
  const [profile, setProfile]           = useState(null);
  const [phone, setPhone]               = useState("");
  const [waPhone, setWaPhone]           = useState("");
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState("");

  // Avatar state
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarMsg, setAvatarMsg]       = useState("");

  // Email SMTP state
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [smtpEmail, setSmtpEmail]       = useState("");
  const [smtpPass, setSmtpPass]         = useState("");
  const [providerIdx, setProviderIdx]   = useState(0);
  const [customHost, setCustomHost]     = useState("");
  const [customPort, setCustomPort]     = useState("465");
  const [showPass, setShowPass]         = useState(false);
  const [savingEmail, setSavingEmail]   = useState(false);
  const [emailMsg, setEmailMsg]         = useState("");
  const [testing, setTesting]           = useState(false);

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    try {
      const [pRes, eRes] = await Promise.all([
        API.get("/users/me/profile"),
        API.get("/users/me/email-settings"),
      ]);
      const p = pRes.data;
      setProfile(p);
      setPhone(p.phone || "");
      setWaPhone(p.whatsapp_phone || "");
      if (p.avatar_url) setAvatarPreview(`${BASE_URL}${p.avatar_url}`);

      const e = eRes.data;
      setEmailConfigured(!!e.smtp_configured);
      setSmtpEmail(e.smtp_email || p.email || "");
      if (e.smtp_host) {
        const idx = PROVIDERS.findIndex(pr => pr.host === e.smtp_host);
        if (idx >= 0) { setProviderIdx(idx); }
        else { setProviderIdx(PROVIDERS.length - 1); setCustomHost(e.smtp_host); setCustomPort(String(e.smtp_port || 465)); }
      } else if (String(p.email || "").toLowerCase().endsWith("@ebekmed.com")) setProviderIdx(1);
    } catch { /* ignore */ }
  }

  // ── Avatar pick & upload ────────────────────────────────────
  function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    uploadAvatar(file);
  }

  async function uploadAvatar(file) {
    setUploadingAvatar(true); setAvatarMsg("");
    const form = new FormData();
    form.append("avatar", file);
    try {
      const res = await API.post("/users/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarPreview(`${BASE_URL}${res.data.avatar_url}`);
      setAvatarMsg("✅ Photo updated");
    } catch (err) {
      setAvatarMsg("❌ " + (err.response?.data?.error || "Upload failed"));
    } finally { setUploadingAvatar(false); }
  }

  // ── Save personal details ───────────────────────────────────
  async function saveProfile() {
    setSaving(true); setSaveMsg("");
    try {
      await API.put("/users/me", { phone: phone.trim(), whatsapp_phone: waPhone.trim() });
      setSaveMsg("✅ Profile saved");
    } catch (e) {
      setSaveMsg("❌ " + (e.response?.data?.error || "Failed to save"));
    } finally { setSaving(false); }
  }

  // ── Save email SMTP settings ────────────────────────────────
  async function saveEmailSettings() {
    const prov = PROVIDERS[providerIdx];
    const isCustom = providerIdx === PROVIDERS.length - 1;
    const host = isCustom ? customHost.trim() : prov.host;
    const port = isCustom ? parseInt(customPort) : prov.port;
    if (!smtpEmail.trim()) { setEmailMsg("❌ Enter your email address"); return; }
    if (!smtpPass.trim())  { setEmailMsg("❌ Enter your App Password");   return; }
    if (!host)             { setEmailMsg("❌ Enter the SMTP host");        return; }
    setSavingEmail(true); setEmailMsg("");
    try {
      await API.put("/users/me/email-settings", {
        smtp_email: smtpEmail.trim(), smtp_password: smtpPass.trim(),
        smtp_host: host, smtp_port: port,
      });
      setEmailConfigured(true);
      setSmtpPass("");
      setEmailMsg("✅ Email connected — CRM emails will now come from your address");
    } catch (e) {
      setEmailMsg("❌ " + (e.response?.data?.error || "Failed to save"));
    } finally { setSavingEmail(false); }
  }

  async function testEmail() {
    setTesting(true); setEmailMsg("");
    try {
      const res = await API.post("/email/test");
      setEmailMsg("✅ " + res.data.message);
    } catch (e) {
      setEmailMsg("❌ " + (e.response?.data?.error || "Test failed — check your credentials"));
    } finally { setTesting(false); }
  }

  async function removeEmail() {
    if (!window.confirm("Remove your email credentials?")) return;
    try {
      await API.delete("/users/me/email-settings");
      setEmailConfigured(false); setSmtpPass("");
      setEmailMsg("Email credentials removed");
    } catch { setEmailMsg("❌ Failed to remove"); }
  }

  const prov = PROVIDERS[providerIdx];
  const initials = profile?.name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div style={{ minHeight:"100vh", background:BG, padding:"0 0 80px" }}>

      {/* ── Top bar ──────────────────────────── */}
      <div style={{ display:"flex", alignItems:"center", padding:"16px 18px 12px", gap:12 }}>
        <button onClick={onBack}
          style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:10,
                   color:"#fff", padding:"8px 14px", fontSize:20, cursor:"pointer" }}>
          ‹
        </button>
        <div>
          <div style={{ color:"#fff", fontWeight:800, fontSize:20 }}>My Profile</div>
          <div style={{ color:"#a5b4fc", fontSize:12 }}>Photo · Contact · Email setup</div>
        </div>
      </div>

      <div style={{ padding:"0 18px" }}>

        {/* ── Avatar card ─────────────────────── */}
        <div style={{ ...CARD, textAlign:"center" }}>
          {/* Avatar circle */}
          <div style={{ position:"relative", display:"inline-block", marginBottom:12 }}>
            <div style={{
              width:90, height:90, borderRadius:"50%", overflow:"hidden",
              background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:34, fontWeight:900, color:"#fff",
              border:"3px solid rgba(255,255,255,0.2)",
            }}>
              {avatarPreview
                ? <img src={avatarPreview} alt="avatar"
                    style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : initials}
            </div>
            {/* Camera overlay */}
            <button onClick={() => fileRef.current?.click()} disabled={uploadingAvatar}
              style={{ position:"absolute", bottom:0, right:0, width:30, height:30,
                       borderRadius:"50%", background:"#6366f1", border:"2px solid #1e1b4b",
                       color:"#fff", fontSize:14, cursor:"pointer", display:"flex",
                       alignItems:"center", justifyContent:"center", padding:0 }}>
              {uploadingAvatar ? "…" : "📷"}
            </button>
            <input ref={fileRef} type="file" accept="image/*"
              style={{ display:"none" }} onChange={handleAvatarChange} />
          </div>

          <div style={{ color:"#fff", fontWeight:800, fontSize:18 }}>{profile?.name || "—"}</div>
          <div style={{ color:"#94a3b8", fontSize:12, marginTop:2 }}>{profile?.email}</div>
          <div style={{ color:"#6366f1", fontSize:11, marginTop:3, textTransform:"uppercase", fontWeight:700 }}>
            {profile?.designation || profile?.role} � {profile?.company}
          </div>
          {avatarMsg && (
            <div style={{ marginTop:8, fontSize:12,
                          color: avatarMsg.startsWith("✅") ? "#86efac" : "#fca5a5" }}>
              {avatarMsg}
            </div>
          )}
          <div style={{ color:"#64748b", fontSize:11, marginTop:6 }}>
            Tap the 📷 to change your photo (max 5 MB)
          </div>
        </div>

        {/* ── Personal details card ───────────── */}
        <div style={CARD}>
          <div style={{ color:"#a5b4fc", fontWeight:700, fontSize:11, letterSpacing:1, marginBottom:16 }}>
            CONTACT DETAILS
          </div>

          <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>Mobile Number</label>
          <input value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="Enter your mobile number"
            style={{ display:"block", width:"100%", marginTop:6, marginBottom:14,
                     padding:"11px 12px", borderRadius:10,
                     border:"1px solid rgba(255,255,255,0.15)",
                     background:"rgba(255,255,255,0.08)", color:"#fff",
                     fontSize:16, boxSizing:"border-box", outline:"none" }} />

          <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>
            💬 WhatsApp Number
            <span style={{ color:"#64748b", fontWeight:400 }}> (for CRM follow-up reminders)</span>
          </label>
          <div style={{ position:"relative", marginTop:6, marginBottom:6 }}>
            <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
                           color:"#94a3b8", fontSize:13 }}>+91</span>
            <input value={waPhone} onChange={e => setWaPhone(e.target.value.replace(/\D/g,""))}
              placeholder="90000 00000"
              maxLength={10}
              style={{ display:"block", width:"100%", padding:"11px 12px 11px 38px",
                       borderRadius:10, border:"1px solid rgba(255,255,255,0.15)",
                       background:"rgba(255,255,255,0.08)", color:"#fff",
                       fontSize:16, boxSizing:"border-box", outline:"none" }} />
          </div>
          <div style={{ color:"#64748b", fontSize:11, marginBottom:16 }}>
            The CRM sends follow-up reminders to this WhatsApp number every morning.
          </div>

          <button onClick={saveProfile} disabled={saving}
            style={{ width:"100%", padding:"11px", borderRadius:10, background:"#6366f1",
                     color:"#fff", border:"none", fontWeight:700, fontSize:14,
                     cursor:"pointer", opacity:saving?0.7:1 }}>
            {saving ? "Saving…" : "Save Details"}
          </button>
          {saveMsg && (
            <div style={{ marginTop:8, fontSize:13, textAlign:"center",
                          color: saveMsg.startsWith("✅") ? "#86efac" : "#fca5a5" }}>
              {saveMsg}
            </div>
          )}
        </div>

        {/* ── Email settings card ─────────────── */}
        <div style={CARD}>
          <div style={{ color:"#a5b4fc", fontWeight:700, fontSize:11, letterSpacing:1, marginBottom:4 }}>
            EMAIL SETTINGS
          </div>
          <div style={{ color:"#94a3b8", fontSize:12, marginBottom:16 }}>
            Connect your personal email so all CRM emails go from YOUR address — recipients can reply directly to you.
          </div>

          {/* Connected badge */}
          {emailConfigured && (
            <div style={{ display:"flex", alignItems:"center", gap:10,
                          background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.3)",
                          borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
              <span style={{ fontSize:20 }}>✅</span>
              <div>
                <div style={{ color:"#6ee7b7", fontWeight:700, fontSize:13 }}>Email connected</div>
                <div style={{ color:"#94a3b8", fontSize:12 }}>Sending from: {smtpEmail}</div>
              </div>
            </div>
          )}

          {/* Provider pills */}
          <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>Email Provider</label>
          <div style={{ display:"flex", gap:8, marginTop:8, marginBottom:14, flexWrap:"wrap" }}>
            {PROVIDERS.map((p, i) => (
              <button key={i} onClick={() => setProviderIdx(i)}
                style={{ padding:"7px 14px", borderRadius:20, fontSize:12, fontWeight:700,
                         cursor:"pointer",
                         border: i===providerIdx ? "1.5px solid #6366f1" : "1px solid rgba(255,255,255,0.15)",
                         background: i===providerIdx ? "#6366f1" : "rgba(255,255,255,0.07)",
                         color:"#fff" }}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Provider hint box */}
          <div style={{ background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.2)",
                        borderRadius:10, padding:"10px 12px", marginBottom:14,
                        fontSize:12, color:"#a5b4fc", lineHeight:1.5 }}>
            💡 {prov.hint}
            {prov.label === "Zoho Mail" && (
              <div style={{ marginTop:8 }}>
                <a href="https://accounts.zoho.com/home#security/security_pwd" target="_blank" rel="noopener noreferrer" style={{ color:"#5eead4", fontWeight:700 }}>Create Zoho app password ↗</a>
                <span style={{ margin:"0 7px", color:"#64748b" }}>·</span>
                <a href="https://www.zoho.com/mail/help/zoho-smtp.html" target="_blank" rel="noopener noreferrer" style={{ color:"#5eead4", fontWeight:700 }}>Zoho SMTP guide ↗</a>
                <div style={{ marginTop:6, color:"#94a3b8" }}>Use your complete @ebekmed.com address, port 465 with SSL. If needed, select port 587 with TLS.</div>
              </div>
            )}
          </div>

          {/* Custom host/port */}
          {providerIdx === PROVIDERS.length - 1 && (
            <div style={{ display:"flex", gap:10, marginBottom:14 }}>
              <div style={{ flex:3 }}>
                <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>SMTP Host</label>
                <input value={customHost} onChange={e => setCustomHost(e.target.value)}
                  placeholder="smtp.yourmail.com"
                  style={{ display:"block", width:"100%", marginTop:6, padding:"10px 12px",
                           borderRadius:10, border:"1px solid rgba(255,255,255,0.15)",
                           background:"rgba(255,255,255,0.08)", color:"#fff", fontSize:13,
                           boxSizing:"border-box", outline:"none" }} />
              </div>
              <div style={{ flex:1 }}>
                <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>Port</label>
                <input value={customPort} onChange={e => setCustomPort(e.target.value)}
                  placeholder="465"
                  style={{ display:"block", width:"100%", marginTop:6, padding:"10px 12px",
                           borderRadius:10, border:"1px solid rgba(255,255,255,0.15)",
                           background:"rgba(255,255,255,0.08)", color:"#fff", fontSize:13,
                           boxSizing:"border-box", outline:"none" }} />
              </div>
            </div>
          )}

          {/* Email address */}
          <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>Your Email Address</label>
          <input value={smtpEmail} onChange={e => setSmtpEmail(e.target.value)}
            placeholder="your.name@ebekindia.com or your.name@ebekmed.com"
            style={{ display:"block", width:"100%", marginTop:6, marginBottom:14,
                     padding:"11px 12px", borderRadius:10,
                     border:"1px solid rgba(255,255,255,0.15)",
                     background:"rgba(255,255,255,0.08)", color:"#fff",
                     fontSize:16, boxSizing:"border-box", outline:"none" }} />

          {/* App Password */}
          <label style={{ color:"#94a3b8", fontSize:12, fontWeight:600 }}>
            App Password&nbsp;
            <span style={{ color:"#6366f1", fontWeight:400 }}>(not your login password)</span>
          </label>
          <div style={{ position:"relative", marginTop:6, marginBottom:6 }}>
            <input type={showPass ? "text" : "password"}
              value={smtpPass} onChange={e => setSmtpPass(e.target.value)}
              placeholder={emailConfigured ? "Enter new App Password to update" : "Paste 16-character App Password"}
              style={{ display:"block", width:"100%", padding:"11px 44px 11px 12px",
                       borderRadius:10, border:"1px solid rgba(255,255,255,0.15)",
                       background:"rgba(255,255,255,0.08)", color:"#fff",
                       fontSize:16, boxSizing:"border-box", outline:"none" }} />
            <button onClick={() => setShowPass(s => !s)}
              style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
                       background:"none", border:"none", color:"#94a3b8", cursor:"pointer", fontSize:18 }}>
              {showPass ? "🙈" : "👁"}
            </button>
          </div>
          <div style={{ color:"#64748b", fontSize:11, marginBottom:16, lineHeight:1.5 }}>
            App Password is a special 16-character code from your email provider's security settings — separate from your regular login password.
          </div>

          {/* Save button */}
          <button onClick={saveEmailSettings} disabled={savingEmail}
            style={{ width:"100%", padding:"12px", borderRadius:10, background:"#6366f1",
                     color:"#fff", border:"none", fontWeight:700, fontSize:14,
                     cursor:"pointer", opacity:savingEmail?0.7:1, marginBottom:10 }}>
            {savingEmail ? "Connecting…" : emailConfigured ? "Update Email Settings" : "Connect My Email"}
          </button>

          {/* Test + Remove buttons (only if already configured) */}
          {emailConfigured && (
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={testEmail} disabled={testing}
                style={{ flex:1, padding:"10px", borderRadius:10,
                         background:"rgba(16,185,129,0.12)", color:"#6ee7b7",
                         border:"1px solid rgba(16,185,129,0.25)",
                         fontWeight:700, fontSize:13, cursor:"pointer", opacity:testing?0.7:1 }}>
                {testing ? "Sending…" : "📨 Send Test Email"}
              </button>
              <button onClick={removeEmail}
                style={{ flex:1, padding:"10px", borderRadius:10,
                         background:"rgba(239,68,68,0.1)", color:"#fca5a5",
                         border:"1px solid rgba(239,68,68,0.25)",
                         fontWeight:700, fontSize:13, cursor:"pointer" }}>
                🗑 Disconnect
              </button>
            </div>
          )}

          {emailMsg && (
            <div style={{ marginTop:12, fontSize:13, textAlign:"center",
                          color: emailMsg.startsWith("✅") ? "#86efac" : "#fca5a5" }}>
              {emailMsg}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
